import { useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useSubmitJobFeedback } from "../opportunities/api.js";
import { BrandLogo } from "../ui/BrandLogo.js";

/**
 * The pro's feedback page, reached from the emailed link at
 * /opportunities/:invitationId/feedback[/:preset]#t=<token>.
 *
 * Same token handling as the magic-link opportunity page: read from the
 * fragment on first render, held in memory, sent only as a header, never
 * written to storage or into a navigated URL.
 *
 * `preset` pre-fills the star the pro tapped in the email. It never
 * auto-submits — a mail client prefetching links must not be able to leave a
 * rating no human chose.
 */
export function OpportunityFeedback() {
  const { invitationId, preset } = useParams<{ invitationId: string; preset?: string }>();
  const location = useLocation();
  const token = useMemo(() => {
    const match = /(?:^|[#&])t=([A-Za-z0-9_-]+)/.exec(location.hash);
    return match?.[1] ?? null;
  }, [location.hash]);

  const submit = useSubmitJobFeedback(invitationId, token);
  const presetRating = preset && /^[1-5]$/.test(preset) ? Number(preset) : undefined;
  const [rating, setRating] = useState<number | undefined>(presetRating);
  const [wouldWorkAgain, setWouldWorkAgain] = useState<boolean | undefined>(undefined);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <Shell>
        <p className="text-charcoal">
          This link is missing its access code. Please open the link from your email directly.
        </p>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <h1 className="text-xl font-bold text-brand-900">Thanks — that's logged.</h1>
        <p className="mt-2 text-charcoal">
          Only our team sees this. It helps us send you better-matched work.
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-xl font-bold text-brand-900">How was this job?</h1>
      <p className="mt-1 text-sm text-charcoal">
        Takes a few seconds, and only our team sees it.
      </p>

      <fieldset className="mt-5">
        <legend className="text-sm font-semibold text-charcoal">Rate the job</legend>
        <div className="mt-2 flex gap-1.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              aria-label={`${n} star${n === 1 ? "" : "s"}`}
              aria-pressed={rating === n}
              onClick={() => setRating(n)}
              className={[
                "min-h-[44px] flex-1 rounded-xl border text-lg font-semibold",
                rating !== undefined && n <= rating
                  ? "border-brand-700 bg-brand-700 text-white"
                  : "border-charcoal/20 bg-white text-charcoal/40",
              ].join(" ")}
            >
              ★
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="mt-5">
        <legend className="text-sm font-semibold text-charcoal">
          Would you take another job from this customer?
        </legend>
        <div className="mt-2 flex gap-2">
          {[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ].map((o) => (
            <button
              key={String(o.value)}
              type="button"
              aria-pressed={wouldWorkAgain === o.value}
              onClick={() => setWouldWorkAgain(o.value)}
              className={[
                "min-h-[44px] flex-1 rounded-xl border px-3 text-sm font-semibold",
                wouldWorkAgain === o.value
                  ? "border-brand-700 bg-brand-50 text-brand-900"
                  : "border-charcoal/20 bg-white text-charcoal",
              ].join(" ")}
            >
              {o.label}
            </button>
          ))}
        </div>
      </fieldset>

      <label className="mt-5 block">
        <span className="text-sm font-semibold text-charcoal">Anything else? (optional)</span>
        <textarea
          value={comment}
          maxLength={1000}
          rows={4}
          onChange={(e) => setComment(e.target.value)}
          className="mt-1 w-full rounded-xl border border-charcoal/20 p-3 text-sm"
          placeholder="Access, communication, payment — whatever's worth knowing next time."
        />
      </label>

      <button
        type="button"
        disabled={rating === undefined || wouldWorkAgain === undefined || submit.isPending}
        onClick={() =>
          submit.mutate(
            {
              rating: rating!,
              wouldWorkAgain: wouldWorkAgain!,
              ...(comment.trim() ? { comment: comment.trim() } : {}),
            },
            { onSuccess: () => setDone(true) },
          )
        }
        className="mt-5 min-h-[44px] w-full rounded-xl bg-brand-700 px-4 text-sm font-semibold text-white disabled:opacity-50"
      >
        {submit.isPending ? "Sending…" : "Send"}
      </button>
      {submit.error && (
        <p className="mt-2 text-sm text-rose-700">{(submit.error as Error).message}</p>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-md px-4 py-8">
      <BrandLogo />
      <div className="mt-6 rounded-2xl border border-charcoal/10 bg-white p-6 shadow-sm">
        {children}
      </div>
    </div>
  );
}
